import { MonitorHeart, MoreHoriz, RocketLaunch, VisibilityOff } from '@mui/icons-material';
import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { ActionButtonLink } from '~/components/button';
import { hiddenHeader } from '~/components/data-grid';
import { SelectAllCellCheckbox } from '~/components/data-grid/SelectAllCellCheckbox';
import { SelectAllHeaderCheckbox } from '~/components/data-grid/SelectAllHeaderCheckbox';
import { DeviceIndicators, DeviceVersion } from '~/components/devices';
import { ActiveShowName } from '~/components/devices/ActiveShowName/ActiveShowName';
import { DeviceName } from '~/components/devices/DeviceName/DeviceName';
import { Link } from '~/components/link';
import { AuthorizedLink } from '~/components/links';
import { useAppContext } from '~/contexts';
import type { RowSelectionMode } from '~/hooks/data-grid';
import { formatDate, formatTimeAgo } from '~/lib/datetime';
import { capitalize } from '~/lib/string';
import type { DeviceList__Device as Device } from '../../queries/list.generated';
import { displaySpace, displayTemperature, formatUptime } from '../../show/lib';
import { Menu } from '../components';
import type { DeviceCommandHandlers } from '../components/CommandPopover';
import { RowCommandButton } from '../components/RowCommandButton';
import { orderColumns } from './helpers';

export interface UseColumnsProps {
  actionHandlers: DeviceCommandHandlers;
  columnOrder: string[];
  columnWidths: Record<string, number>;
  handleRowSelectionModeChange: (mode: RowSelectionMode) => void;
  rowSelectionMode: RowSelectionMode;
  selectableCount: number;
}

export const useColumns = ({
  actionHandlers,
  columnOrder,
  columnWidths,
  handleRowSelectionModeChange,
  rowSelectionMode,
  selectableCount,
}: UseColumnsProps): GridColDef<Device>[] => {
  const { currentNetwork, currentUser } = useAppContext();
  const { t } = useTranslation();

  const theme = useTheme();
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  return useMemo(() => {
    const cols: GridColDef<Device>[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        align: 'left',
        headerAlign: 'left',
        hideable: false,
        width: 75,
        renderCell: (params) => (
          <SelectAllCellCheckbox
            {...params}
            handleRowSelectionModeChange={handleRowSelectionModeChange}
            label={t('common.actions.select_item', { name: params.row.name })}
          />
        ),
        renderHeader: (params) => {
          if (selectableCount === 0) return null;
          return (
            <SelectAllHeaderCheckbox
              {...params}
              handleRowSelectionModeChange={handleRowSelectionModeChange}
              selectableCount={selectableCount}
              rowSelectionMode={rowSelectionMode}
            />
          );
        },
      },
      {
        field: 'Indicators',
        ...hiddenHeader(t('common.labels.status')),
        sortable: false,
        resizable: false,
        renderCell: ({ row }) => <DeviceIndicators device={row} />,
      },
      {
        field: 'id',
        headerName: 'ID',
        hideable: false,
        width: 65,
        resizable: false,
      },
      {
        field: 'name',
        flex: 1,
        headerName: t('common.labels.name'),
        minWidth: isSmallAndUp ? 180 : 150,
        // The row's own click handler is mouse-only, so the name is the row's keyboard path to
        // the device. The actions column's link to it is hidden on narrow screens. The explicit
        // tabindex makes the grid focus the link, not the cell, when the arrow keys reach it.
        renderCell: ({ row }) => (
          <Link
            to={`${row.id}/details`}
            // Stop the row's onClick, or a click would navigate twice.
            onClick={(event) => event.stopPropagation()}
            color="inherit"
            underline="hover"
            sx={{ display: 'flex', minWidth: 0 }}
            tabIndex={0}
          >
            <DeviceName device={row} />
          </Link>
        ),
      },
      {
        field: 'group',
        flex: 1,
        headerName: t('common.labels.group'),
        minWidth: 100,
        resizable: true,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<Device>) => (
          <AuthorizedLink authorized={currentNetwork.canManage.value} entity={row.group} />
        ),
      },
      {
        field: 'activeShow',
        flex: 1,
        headerName: t('pages.devices.list.columns.now_playing'),
        minWidth: 150,
        resizable: true,
        sortable: true,
        renderCell: ({ row }: GridRenderCellParams<Device>) => (
          <ActiveShowName activeShow={row.activeShow} />
        ),
      },
      {
        field: 'ipAddress',
        headerName: t('common.labels.ip_address'),
        minWidth: 120,
        resizable: true,
        sortable: true,
      },
      {
        field: 'playerVersion',
        headerName: t('common.labels.version'),
        minWidth: 150,
        renderCell: ({ row }) => <DeviceVersion device={row} />,
        resizable: true,
        sortable: true,
      },
      {
        field: 'model',
        headerName: t('pages.devices.list.columns.model'),
        minWidth: 100,
        valueGetter: (_, row) => row.model ?? '--',
        resizable: true,
        sortable: true,
      },
      // Sysadmin-only: omitted rather than hidden, so the column picker can't reveal it.
      ...(currentUser.admin
        ? [
            {
              field: 'displayServer',
              headerName: t('pages.devices.list.columns.display_server'),
              minWidth: 130,
              valueGetter: (_, row) => (row.displayServer ? capitalize(row.displayServer) : '--'),
              resizable: true,
              sortable: true,
            } satisfies GridColDef<Device>,
          ]
        : []),
      {
        field: 'connected',
        headerName: t('pages.devices.list.columns.last_checked_in'),
        minWidth: 225,
        renderCell: ({ row }) => {
          if (!row.statusUpdatedAt) return t('pages.devices.list.columns.never_connected');
          return (
            <Tooltip title={formatTimeAgo(row.statusUpdatedAt)}>
              <span>{formatDate(row.statusUpdatedAt)}</span>
            </Tooltip>
          );
        },
        resizable: true,
        sortable: true,
      },
      {
        field: 'processUptime',
        headerName: t('pages.devices.list.columns.software_uptime'),
        minWidth: 150,
        valueGetter: (_, row) => formatUptime(row.processUptime),
        resizable: false,
        sortable: true,
      },
      {
        field: 'osUptime',
        headerName: t('pages.devices.list.columns.hardware_uptime'),
        minWidth: 150,
        valueGetter: (_, row) => formatUptime(row.osUptime),
        resizable: false,
        sortable: true,
      },
      {
        field: 'internal',
        ...hiddenHeader(t('common.labels.type')),
        hideable: false,
        minWidth: 120,
        resizable: false,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              {row.internal === true && (
                <Chip
                  icon={<VisibilityOff />}
                  label={t('common.labels.internal')}
                  color="primary"
                />
              )}
              {row.demo === true && (
                <Chip icon={<RocketLaunch />} label={t('common.labels.demo')} color="tertiary" />
              )}
            </Box>
          );
        },
      },
      {
        field: 'cpuPercentage',
        headerName: t('pages.devices.show.additional.cpu_usage'),
        valueGetter: (_, { cpuPercentage }) => (cpuPercentage ? `${cpuPercentage}%` : '--'),
      },
      {
        field: 'cpuTemperature',
        headerName: t('pages.devices.show.additional.cpu_temperature'),
        valueGetter: (_, { cpuTemperature }) => displayTemperature(cpuTemperature, '--'),
      },
      {
        field: 'diskUsage',
        headerName: t('pages.devices.show.additional.disk'),
        valueGetter: (_, { diskAvailable, diskTotal }) =>
          displaySpace(diskAvailable, diskTotal, '--'),
      },
      {
        field: 'memoryUsage',
        headerName: t('pages.devices.show.additional.memory'),
        valueGetter: (_, { memoryAvailable, memoryTotal }) =>
          displaySpace(memoryAvailable, memoryTotal, '--'),
      },
      {
        align: 'right',
        field: 'actions',
        hideable: false,
        type: 'actions',
        resizable: false,
        width: isSmallAndUp ? 150 : 50,
        getActions: ({ row }) => {
          const actions = [];

          if (row.canUpdate.value)
            actions.push(
              <RowCommandButton key={`send-command-${row.id}`} device={row} {...actionHandlers} />,
            );

          if (isSmallAndUp) {
            actions.push(
              <ActionButtonLink
                key={`dashboard-${row.id}`}
                title={t('pages.devices.list.actions.dashboard')}
                Icon={MonitorHeart}
                to={`${row.id}/details`}
              />,
            );
            actions.push(
              <Menu
                key={`more-action-${row.id}`}
                device={row}
                Icon={MoreHoriz}
                buttonType="action"
              />,
            );
          }
          // Direct children, not wrapped: the actions cell moves focus between its children.
          return actions;
        },
        sortable: false,
      },
    ];
    const sized = cols.map((col) =>
      col.field && columnWidths[col.field]
        ? { ...col, flex: undefined, width: columnWidths[col.field] }
        : col,
    );
    return orderColumns(sized, columnOrder);
  }, [
    actionHandlers,
    columnOrder,
    columnWidths,
    currentNetwork,
    currentUser,
    handleRowSelectionModeChange,
    isSmallAndUp,
    rowSelectionMode,
    selectableCount,
    t,
  ]);
};
